/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import seriesesConfig from 'config/constants/serieses'
import tokens from 'config/constants/tokens'
import { SECOND_PER_YEAR_NUM } from 'config'
import { SeriesesState, Series } from 'state/types'
import { formatDateFromObject } from 'utils'
import { getAddress, getDmdAddress } from 'utils/addressHelpers'
import fetchPublicSeriesesData from './fetchPublicSeriesData'
import fetchSeriesesUserData from './fetchSeriesesUser'

const initialState: SeriesesState = {
  data: [...seriesesConfig],
  userDataLoaded: false,
}

export const fetchSeriesesPublicDataAsync = () => async (dispatch, getState) => {
  const prices = getState().farms.prices
  const currentSerieses = getState().serieses.data
  const seriesesPublicData = await fetchPublicSeriesesData(currentSerieses)
  const liveData = seriesesPublicData.map((seriesPublicData, index) => {
    const buyTokenAddress =
      currentSerieses[index].token && !seriesPublicData.marketPrice
        ? getAddress(currentSerieses[index].token.address).toLowerCase()
        : getAddress(tokens.wftm.address).toLowerCase()
    const buyTokenPrice = prices[buyTokenAddress]

    const rewardTokenAddress = currentSerieses[index].rewardToken
      ? getAddress(currentSerieses[index].rewardToken.address).toLowerCase()
      : getDmdAddress().toLowerCase()
    const rewardTokenPrice = prices[rewardTokenAddress]

    const tvl =
      seriesPublicData.totalStaked * (buyTokenPrice || 0) * (seriesPublicData.marketPrice || seriesPublicData.price)
    const apr = tvl
      ? ((seriesPublicData.tokenPerSecond * (rewardTokenPrice || 0) * SECOND_PER_YEAR_NUM) / tvl) * 100
      : 0

    const rewardRemained = seriesPublicData.totalMintAmount - seriesPublicData.mintedAmount
    const remainSecond = rewardRemained / seriesPublicData.tokenPerSecond
    const rewardsEndDate = currentSerieses[index].isMasterChefPool
      ? null
      : formatDateFromObject(new Date(new Date().getTime() + remainSecond * 1000))

    return {
      ...currentSerieses[index],
      ...seriesPublicData,
      apr,
      rewardTokenPrice,
      isFinished:
        !!currentSerieses[index].pool &&
        !currentSerieses[index].isMasterChefPool &&
        ((currentSerieses[index].withoutMC && seriesPublicData.stakingPaused) ||
          (!currentSerieses[index].withoutMC &&
            seriesPublicData.startStakingTime < +new Date() / 1000 &&
            (seriesPublicData.stakingPaused || rewardRemained === 0))),
      rewardsEndDate,
      rewardRemained,
    }
  })

  dispatch(setSeriesesPublicData(liveData))
}

export const fetchSeriesesUserDataAsync =
  (account: string): any =>
  async (dispatch) => {
    // const userDataLoaded = getState().serieses.userDataLoaded
    // if (userDataLoaded) {
    //   return
    // }
    // const currentSerieses = getState().serieses.data
    const userData = await fetchSeriesesUserData(seriesesConfig, account)

    dispatch(setSeriesesUserData(userData))
  }

export const updateUserNftsAfterTransfer =
  (sid: number, tokenId: number): any =>
  async (dispatch, getState) => {
    const currentSerieses = getState().serieses.data
    const seriesToFetch = currentSerieses.find((series) => series.sid === sid)

    const userNftsForSeries = seriesToFetch.userData?.nfts?.filter((nft) => nft.nid !== tokenId)

    dispatch(
      updateSeriesesUserData({
        sid,
        approved: seriesToFetch.userData?.approved || false,
        // balance: seriesToFetch.userData?.balance - 1 || 0,
        nfts: userNftsForSeries,
      }),
    )
  }

export const updateUserNftsApproved =
  (sid: number): any =>
  async (dispatch, getState) => {
    const currentSerieses = getState().serieses.data
    const seriesToFetch = currentSerieses.find((series) => series.sid === sid)

    dispatch(
      updateSeriesesUserData({
        sid,
        approved: true,
        // balance: seriesToFetch.userData?.balance || 0,
        nfts: seriesToFetch.userData?.nfts || [],
      }),
    )
  }

export const updateUserNftsStaked =
  (sid: number, tokenId: number): any =>
  async (dispatch, getState) => {
    const currentSerieses = getState().serieses.data
    const seriesToFetch = currentSerieses.find((series) => series.sid === sid)
    // let newBalance = seriesToFetch.userData?.balance || 0

    const userNftsForSeries = seriesToFetch.userData?.nfts?.map((nft) => {
      if (nft.nid !== tokenId) {
        return { ...nft }
      }
      // if (nft.isStaked) {
      //   newBalance++
      // } else {
      //   newBalance--
      // }
      return {
        ...nft,
        isStaked: !nft.isStaked,
      }
    })

    dispatch(
      updateSeriesesUserData({
        sid,
        approved: seriesToFetch.userData?.approved || false,
        // balance: newBalance,
        nfts: userNftsForSeries,
      }),
    )
  }

export const updateUserNftsAfterMultiStake =
  (sid: number, staked: boolean, tokenIds: number[]): any =>
  async (dispatch, getState) => {
    const currentSerieses = getState().serieses.data
    const seriesToFetch = currentSerieses.find((series) => series.sid === sid)
    // let newBalance = seriesToFetch.userData?.balance || 0

    const userNftsForSeries = seriesToFetch.userData?.nfts?.map((nft) => ({
      ...nft,
      isStaked: tokenIds.find((nid) => nid === nft.nid) ? staked : nft.isStaked,
    }))

    dispatch(
      updateSeriesesUserData({
        sid,
        approved: seriesToFetch.userData?.approved || false,
        // balance: newBalance,
        nfts: userNftsForSeries,
      }),
    )
  }

export const SeriesesSlice = createSlice({
  name: 'Serieses',
  initialState,
  reducers: {
    setSeriesesPublicData: (state, action) => {
      const liveSeriesesData: Series[] = action.payload
      state.data = state.data.map((series) => {
        const liveSeriesData = liveSeriesesData.find((entry) => entry.sid === series.sid)
        return { ...series, ...liveSeriesData }
      })
    },
    setSeriesesUserData: (state, action) => {
      const userData = action.payload
      state.data = state.data.map((series) => {
        const userSeriesData = userData.find((entry) => entry.sid === series.sid)
        return {
          ...series,
          userData: {
            ...userSeriesData,
          },
        }
      })
      state.userDataLoaded = true
    },
    updateSeriesesUserData: (state, action) => {
      // const { nfts, balance, sid, approved } = action.payload
      const { nfts, sid, approved } = action.payload
      const index = state.data.findIndex((s) => s.sid === sid)

      if (index >= 0) {
        state.data[index] = {
          ...state.data[index],
          userData: {
            ...state.data[index].userData,
            // balance,
            nfts,
            approved,
          },
        }
      }
    },
  },
})

// Actions
export const { setSeriesesPublicData, setSeriesesUserData, updateSeriesesUserData } = SeriesesSlice.actions

export default SeriesesSlice.reducer
